import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { CartItemsComponent } from '@theme/component-cart-items';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

const instances = new Set();
const pendingEvents = new Set();
const pendingQuantityChanges = new Set();
const productStates = new Map();
let active = false;

function syncInstances() {
  for (const instance of instances) instance.sync();
}

function itemProductId(item) {
  return String(item.product_id ?? item.product?.id ?? '');
}

function containsProduct(cart, productId) {
  return cart.items.some((item) => itemProductId(item) === productId && Number(item.quantity) > 0);
}

function trackCartEvent(event) {
  if (!event.promise) return;
  const pending = Promise.resolve(event.promise);
  pendingEvents.add(pending);
  pending.then(({ detail } = {}) => {
    if (!Array.isArray(detail?.items)) return;
    for (const [productId, state] of productStates) {
      if (state === 'added' && !detail.items.some((item) => itemProductId(item) === productId)) {
        productStates.delete(productId);
      }
    }
    syncInstances();
  }).catch(() => {}).finally(() => pendingEvents.delete(pending));
}

document.addEventListener(StandardEvents.cartLinesUpdate, trackCartEvent);
document.addEventListener(StandardEvents.cartDiscountUpdate, trackCartEvent);
document.addEventListener('quantity-selector:update', () => {
  const settling = new Promise((resolve) => setTimeout(resolve, 350));
  pendingQuantityChanges.add(settling);
  settling.finally(() => pendingQuantityChanges.delete(settling));
});

async function settleCartUpdates() {
  while (pendingQuantityChanges.size || pendingEvents.size || CartItemsComponent.pendingQuantityUpdates.length) {
    await Promise.allSettled([
      ...pendingQuantityChanges,
      ...pendingEvents,
      ...CartItemsComponent.pendingQuantityUpdates,
    ]);
  }
}

function cartURL() {
  return `${Theme.routes.cart_url.replace(/\/$/, '')}.js`;
}

async function timedFetch(url, options) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  try { return await fetch(url, { ...options, signal: controller.signal }); }
  finally { clearTimeout(timer); }
}

async function readCart() {
  const response = await timedFetch(cartURL(), {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Cart could not be read');
  const cart = await response.json();
  if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
  return cart;
}

function cartSections() {
  return [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
}

async function renderOwner(owner, sections) {
  if (!owner?.isConnected || !owner.dataset.sectionId) return;
  const sectionId = owner.dataset.sectionId;
  const options = {
    mode: owner.hasAttribute('data-drawer') ? 'hydration' : 'full',
    injectStylesheet: !!owner.querySelector('[data-cart-drawer-empty]'),
  };
  sectionRenderer.abortRender(sectionId);
  if (typeof sections?.[sectionId] === 'string' && sections[sectionId]) {
    try {
      await morphSection(sectionId, sections[sectionId], options);
      return;
    } catch (_) {}
  }
  await sectionRenderer.renderSection(sectionId, { ...options, cache: false });
}

function dispatchCartResult(owner, deferred, cart, sections, productId, didError) {
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      source: 'luke-cart-complements',
      productId,
      itemCount: cart.item_count,
      items: cart.items,
      sections,
      didError,
    },
  });
  return renderOwner(owner, sections).then(() => {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) selector.updateButtonStates?.();
  });
}

class LukeCartComplements extends HTMLElement {
  connectedCallback() {
    if (instances.has(this)) return;
    instances.add(this);
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    for (const card of this.querySelectorAll('[data-lcc-card]')) this.rememberCard(card);
    this.sync();
  }

  rememberCard(card) {
    if (!card._lccOriginal || card._lccOriginal.productId !== card.dataset.productId) {
      card._lccOriginal = {
        productId: card.dataset.productId,
        price: card.querySelector('[data-lcc-price]')?.textContent ?? '',
        image: card.querySelector('[data-lcc-image]')?.getAttribute('src') ?? '',
        links: [...card.querySelectorAll('[data-lcc-link]')].map((link) => link.getAttribute('href')),
      };
    }
  }

  disconnectedCallback() {
    instances.delete(this);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
  }

  label(name, fallback) {
    return this.dataset[name] || fallback;
  }

  status(card, message) {
    const status = card.querySelector('[data-lcc-status]') || this.querySelector('[data-lcc-status]');
    if (status) status.textContent = message;
  }

  selection(card) {
    const field = card.querySelector('[data-lcc-variant]');
    if (!field || !/^\d+$/.test(field.value)) return null;
    const option = field.tagName === 'SELECT'
      ? [...field.options].find((item) => item.value === field.value)
      : field;
    if (!option || option.disabled) return null;
    return { field, option, id: field.value };
  }

  sync() {
    for (const card of this.querySelectorAll('[data-lcc-card]')) {
      const button = card.querySelector('[data-lcc-add]');
      const field = card.querySelector('[data-lcc-variant]');
      if (!button) continue;
      this.rememberCard(card);
      const state = productStates.get(card.dataset.productId);
      const checking = state === 'check';
      button.disabled = active || state === 'added' || (!checking && !this.selection(card));
      const label = active && card.dataset.lccPending === 'true'
        ? this.label('addingLabel', 'Adding…')
        : checking ? this.label('checkLabel', 'Check cart')
          : state === 'added' ? this.label('addedLabel', 'Added')
            : this.label('addLabel', 'Add');
      if (button.textContent !== label) button.textContent = label;
      if (field?.tagName === 'SELECT') field.disabled = active || !!state;
      button.setAttribute('aria-busy', String(active && card.dataset.lccPending === 'true'));
    }
  }

  applySelection(card) {
    this.rememberCard(card);
    const selected = this.selection(card);
    const original = card._lccOriginal;
    const price = card.querySelector('[data-lcc-price]');
    const image = card.querySelector('[data-lcc-image]');
    const text = selected?.option.dataset.priceFormatted || original?.price || '';
    if (price && price.textContent !== text) price.textContent = text;
    if (image) {
      const source = selected?.option.dataset.image || original?.image;
      if (source) {
        if (image.getAttribute('src') !== source) image.setAttribute('src', source);
        image.removeAttribute('srcset');
      }
    }
    [...card.querySelectorAll('[data-lcc-link]')].forEach((link, index) => {
      const url = selected?.option.dataset.url || original?.links[index];
      if (url && link.getAttribute('href') !== url) link.setAttribute('href', url);
    });
  }

  onChange = (event) => {
    if (!event.target.matches('[data-lcc-variant]')) return;
    const card = event.target.closest('[data-lcc-card]');
    if (!card || card.closest('luke-cart-complements') !== this) return;
    this.applySelection(card);
    this.status(card, '');
    this.sync();
  };

  onClick = (event) => {
    const button = event.target.closest('[data-lcc-add]');
    if (!button || button.closest('luke-cart-complements') !== this) return;
    event.preventDefault();
    if (active || button.disabled) return;
    const card = button.closest('[data-lcc-card]');
    if (card) this.add(card);
  };

  async add(card) {
    const productId = card.dataset.productId;
    const checking = productStates.get(productId) === 'check';
    const selected = this.selection(card);
    if (active || !/^\d+$/.test(productId) || (!checking && !selected)) return;
    active = true;
    card.dataset.lccPending = 'true';
    this.status(card, '');
    syncInstances();
    let owner;
    let deferred;
    let attempted = false;
    let rejected = false;
    let added = false;
    let mutationSections;
    let ownerWasDisabled = false;
    try {
      await settleCartUpdates();
      owner = this.closest('cart-items-component');
      if (!this.isConnected || !card.isConnected || card.closest('luke-cart-complements') !== this || !owner?.dataset.sectionId) return;
      ownerWasDisabled = owner.classList.contains('cart-items-disabled');
      owner.classList.add('cart-items-disabled');
      const before = await readCart();
      if (checking || containsProduct(before, productId)) {
        added = containsProduct(before, productId);
        productStates.set(productId, added ? 'added' : 'check');
        deferred = CartLinesUpdateEvent.createPromise();
        deferred.promise.catch(() => {});
        owner.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise }));
        await dispatchCartResult(owner, deferred, before, undefined, productId, !added);
        this.status(card, added ? this.label('addedLabel', 'Added') : this.label('pendingLabel', 'We could not confirm the update. Check your cart before trying again.'));
        return;
      }
      if (!card.isConnected || card.closest('luke-cart-complements') !== this || !this.selection(card) || selected.id !== this.selection(card).id) return;
      const sections = cartSections();
      for (const sectionId of sections) sectionRenderer.abortRender(sectionId);
      deferred = CartLinesUpdateEvent.createPromise();
      deferred.promise.catch(() => {});
      owner.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'cart',
        lines: [{ merchandiseId: selected.id, quantity: 1 }], promise: deferred.promise,
      }));
      attempted = true;
      const response = await timedFetch(Theme.routes.cart_add_url, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(selected.id), quantity: 1 }], sections: sections.join(','), sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status || result.errors) {
        rejected = [400, 401, 403, 404, 422].includes(Number(response.status || result.status));
        throw new Error('The selected item could not be added');
      }
      added = true;
      productStates.set(productId, 'added');
      mutationSections = result.sections;
      const after = await readCart();
      if (!containsProduct(after, productId)) throw new Error('The cart has not confirmed the added item');
      await dispatchCartResult(owner, deferred, after, mutationSections, productId, false);
      this.status(card, this.label('addedLabel', 'Added'));
    } catch (error) {
      let recovered;
      if (attempted) {
        try { recovered = await readCart(); } catch (_) {}
      }
      if (recovered && containsProduct(recovered, productId)) {
        added = true;
        productStates.set(productId, 'added');
        try {
          await dispatchCartResult(owner, deferred, recovered, undefined, productId, false);
          this.status(card, this.label('addedLabel', 'Added'));
        } catch (_) {
          productStates.set(productId, 'check');
          this.status(card, `${this.label('addedLabel', 'Added')}. ${this.label('checkLabel', 'Check cart')}.`);
        }
      } else {
        if (attempted && (!rejected || !recovered || added)) productStates.set(productId, 'check');
        else if (!checking) productStates.delete(productId);
        if (deferred) {
          if (recovered) {
            try { await dispatchCartResult(owner, deferred, recovered, undefined, productId, true); } catch (_) {}
          } else deferred.reject(error);
        }
        this.status(card, added
          ? `${this.label('addedLabel', 'Added')}. ${this.label('checkLabel', 'Check cart')}.`
          : productStates.get(productId) === 'check'
            ? this.label('pendingLabel', 'We could not confirm the update. Check your cart before trying again.')
            : this.label('errorLabel', 'Could not add this item. Please try again.'));
        (owner?.isConnected ? owner : document).dispatchEvent(new CartErrorEvent({
          error: this.label('errorLabel', 'Could not add this item'),
          code: rejected ? 'INVALID' : 'SERVICE_UNAVAILABLE',
        }));
      }
    } finally {
      if (owner && !ownerWasDisabled) owner.classList.remove('cart-items-disabled');
      delete card.dataset.lccPending;
      active = false;
      syncInstances();
    }
  }
}

if (!customElements.get('luke-cart-complements')) customElements.define('luke-cart-complements', LukeCartComplements);
